/**
 * Occupancy timing utilities
 * Derives heating behavior characteristics from occupancy timing patterns
 */

import type { OccupancyTiming, HeatingStyleHint, SetbackTolerance } from './types';

/**
 * Derive heating style hint from occupancy timing pattern
 * 
 * Deterministic mapping based on when the home is occupied:
 * - evenings_weekends → bursty (short heating periods)
 * - mornings_evenings → bursty (burst heating before/after work)
 * - most_of_day → steady (continuous heating needed)
 * - variable/unknown → mixed (moderate approach)
 */
export function deriveHeatingStyleHint(timing?: OccupancyTiming): HeatingStyleHint {
  if (!timing || timing === 'unknown') return 'mixed';
  
  switch (timing) {
    case 'evenings_weekends':
      return 'bursty';
    case 'mornings_evenings':
      return 'bursty';
    case 'most_of_day':
      return 'steady';
    case 'variable':
      return 'mixed';
    default:
      return 'mixed';
  }
}

/**
 * Derive setback tolerance from occupancy timing pattern
 * 
 * Determines how much temperature setback the occupancy pattern can tolerate:
 * - evenings_weekends → high (home empty weekdays, can cool down significantly)
 * - mornings_evenings → medium (away during work hours, moderate setback ok)
 * - most_of_day → low (home most of time, prefer steady temperature)
 * - variable/unknown → medium (moderate approach)
 */
export function deriveSetbackTolerance(timing?: OccupancyTiming): SetbackTolerance {
  if (!timing || timing === 'unknown') return 'medium';
  
  switch (timing) {
    case 'evenings_weekends':
      return 'high';
    case 'mornings_evenings':
      return 'medium';
    case 'most_of_day':
      return 'low';
    case 'variable':
      return 'medium';
    default:
      return 'medium';
  }
}

/**
 * Get human-readable label for occupancy timing pattern
 * For use in presentation and summary text
 */
export function getOccupancyTimingLabel(timing?: OccupancyTiming): string {
  if (!timing || timing === 'unknown') return 'Occupancy pattern not specified';
  
  switch (timing) {
    case 'evenings_weekends':
      return 'Typically occupied in the evenings and at weekends';
    case 'mornings_evenings':
      return 'Typically occupied in the mornings and evenings';
    case 'most_of_day':
      return 'Typically occupied most of the day';
    case 'variable':
      return 'Occupancy varies day to day';
    default:
      return 'Occupancy pattern not specified';
  }
}

/**
 * Determine if occupancy timing indicates daytime presence
 */
export function hasDaytimeOccupancy(timing?: OccupancyTiming): boolean {
  return timing === 'most_of_day';
}
